import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { postNewsletterRequest } from "@/api/newsletter";
import { ApiError } from "@/api/client";
import inviteHero from "@/assets/invite-hero.png";

export default function InviteSection() {
  const [email, setEmail] = useState("");
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const subscribeMutation = useMutation({
    mutationFn: (subscriberEmail: string) =>
      postNewsletterRequest(subscriberEmail, "alta"),
    onSuccess: () => {
      setFeedback({
        type: "success",
        message: "Solicitud de alta registrada. Te avisaremos cuando esté procesada.",
      });
      setEmail("");
    },
    onError: (err: unknown) => {
      setFeedback({
        type: "error",
        message:
          err instanceof ApiError ? err.message : "No se pudo registrar la solicitud.",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || subscribeMutation.isPending) return;
    setFeedback(null);
    subscribeMutation.mutate(trimmed);
  };

  return (
    <section className="pulse-invite mx-auto w-full max-w-[1728px] px-5 pb-[var(--pulse-invite-to-footer-gap)] max-md:px-0 max-lg:pb-0 md:px-8 max-xs:pb-0 lg:px-10">
      <div className="pulse-invite-card">
        <div className="pulse-invite-media overflow-hidden bg-[rgba(217,217,217,0.9)]">
          <img
            src={inviteHero}
            alt=""
            className="h-full w-full object-cover"
            width={645}
            height={710}
            decoding="async"
          />
        </div>

        <h2 className="pulse-invite-title">
          <span className="font-bold">Acceso </span>
          <span className="font-light">limitado por </span>
          <span className="font-bold">invitación.</span>
        </h2>

        <div className="pulse-invite-copy">
          <p className="pulse-invite-lead m-0 text-[24px] font-medium leading-[normal] text-[#003F28]">
            Cada día, lo esencial sobre inteligencia artificial y publicidad. Sin ruido. Solo lo que
            importa. Comparte el pulso.
          </p>

          <form className="pulse-invite-form" onSubmit={handleSubmit}>
            <label htmlFor="invite-email" className="sr-only">
              Correo electrónico
            </label>
            <input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (feedback) setFeedback(null);
              }}
              placeholder="email@iberdrola.com"
              className="pulse-invite-input"
              disabled={subscribeMutation.isPending}
            />
            <button
              type="submit"
              aria-label="Enviar invitación"
              className="pulse-invite-submit"
              disabled={!email.trim() || subscribeMutation.isPending}
            >
              <svg
                className="pulse-invite-submit-icon"
                width={56}
                height={56}
                viewBox="0 0 56 56"
                fill="none"
                aria-hidden="true"
              >
                <circle cx="28" cy="28" r="28" fill="#FFA61C" />
                <g className="access-form__submit-arrow">
                  <path
                    d="M27.8906 17.78L37.8976 28L27.8906 38.22"
                    stroke="#003F28"
                    strokeWidth={2}
                  />
                  <path d="M38.22 28.0278L17.78 28.0278" stroke="#003F28" strokeWidth={2} />
                </g>
              </svg>
            </button>
          </form>

          {feedback ? (
            <p
              className={`mt-4 text-sm ${
                feedback.type === "success" ? "text-emerald-800" : "text-red-700"
              }`}
              role="status"
            >
              {feedback.message}
            </p>
          ) : null}
        </div>
      </div>
    </section>
  );
}
